/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useDispatch, useSelector } from 'react-redux';
import { ENVIRONMENT } from 'config';
import {
  selectSummaryFilterList,
  selectPaceVisualFilterList,
  selectSummaryVisualToggle,
  selectPaceVisualIsDisplay,
  selectRevenueSetIsDisplay,
  selectCxlSetIsDisplay,
  selectEnableSecondaryHotelGroups,
  selectSummaryHotelComparison,
} from 'modules/dashboard/components/tab-container/summary-tab/selectors';
import { AssistantIcon, CustomToggleSwitch } from 'modules/common/components';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import summaryStyles from 'modules/dashboard/components/tab-container/summary-tab/summary.module.scss';
import { summaryActions } from 'modules/dashboard/components/tab-container/summary-tab/slice';
import VisualGrid from 'modules/dashboard/components/tab-container/visual-grid';

/**
 * Summary revenue, cancellation and pace visuals with display and export controls
 * @param {Function} getWidget - Builds an embedded visual from its configured order
 * @param {Function} getVisualName - Gets the display name of a configured visual
 * @param {Object} revenue - Revenue report, render and processing state with setters
 * @param {Object} cancellations - Cancellation report, render and processing state with setters
 * @param {Object} pace - Pace report, render and processing state with setters
 * @param {Function} setIsVisualCard1Loaded - Updates revenue visual load state
 * @param {Function} setIsVisualCard2Loaded - Updates cancellation visual load state
 * @param {Function} setIsPaceVisualLoaded - Updates pace visual load state
 * @param {Boolean} visualToggleDisabled - Extended forecasts toggle disabled state
 * @param {String} exportType - Selected export option shared by the visuals
 * @param {Function} setExportType - Updates the selected export option
 * @returns {JSX.Element} Summary visual grids
 */
const SummaryVisuals = ({
  getWidget,
  getVisualName,
  revenue,
  cancellations,
  pace,
  setIsVisualCard1Loaded,
  setIsVisualCard2Loaded,
  setIsPaceVisualLoaded,
  visualToggleDisabled,
  exportType,
  setExportType,
}) => {
  const dispatch = useDispatch();
  const summaryFilterList = useSelector(selectSummaryFilterList);
  const paceVisualFilterList = useSelector(selectPaceVisualFilterList);
  const visualToggle = useSelector(selectSummaryVisualToggle);
  const paceVisualIsDisplay = useSelector(selectPaceVisualIsDisplay);
  const revenueSetIsDisplay = useSelector(selectRevenueSetIsDisplay);
  const cxlSetIsDisplay = useSelector(selectCxlSetIsDisplay);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const isSummaryHotelComparison = useSelector(selectSummaryHotelComparison);
  return (
    <>
      <VisualGrid
        isDisplay={revenueSetIsDisplay}
        visualName={getVisualName(0)}
        visual={getWidget(
          0,
          summaryStyles.VisualCard,
          revenue.setReport,
          setIsVisualCard1Loaded,
          summaryFilterList,
          revenue.setIsRendered,
          summaryActions.setRevenueSetIsDisplay
        )}
        onClick={() => {
          dispatch(summaryActions.setRevenueSetIsDisplay(!revenueSetIsDisplay));
        }}
        interpreterFragment={
          <AssistantIcon
            visualName={getVisualName(0)}
            report={revenue.report}
            isProcessing={revenue.isProcessing}
            setIsProcessing={revenue.setIsProcessing}
            disabled={!revenue.isRendered || revenue.isProcessing || !revenueSetIsDisplay}
            enableSecondaryHotelGroups={enableSecondaryHotelGroups}
            exportType={exportType}
            setExportType={setExportType}
          />
        }
      />
      <VisualGrid
        isDisplay={cxlSetIsDisplay}
        visualName={getVisualName(1)}
        visual={getWidget(
          1,
          summaryStyles.VisualCard,
          cancellations.setReport,
          setIsVisualCard2Loaded,
          summaryFilterList,
          cancellations.setIsRendered,
          summaryActions.setCxlSetIsDisplay
        )}
        onClick={() => {
          dispatch(summaryActions.setCxlSetIsDisplay(!cxlSetIsDisplay));
        }}
        interpreterFragment={
          <AssistantIcon
            visualName={getVisualName(1)}
            report={cancellations.report}
            isProcessing={cancellations.isProcessing}
            setIsProcessing={cancellations.setIsProcessing}
            disabled={
              !cancellations.isRendered ||
              cancellations.isProcessing ||
              !cxlSetIsDisplay ||
              isSummaryHotelComparison
            }
            enableSecondaryHotelGroups={enableSecondaryHotelGroups}
            exportType={exportType}
            setExportType={setExportType}
          />
        }
      />
      <VisualGrid
        isDisplay={paceVisualIsDisplay}
        visualName={getVisualName(visualToggle ? 4 : 2)}
        visual={
          <>
            {visualToggle &&
              getWidget(
                4,
                styles.paceVisual,
                pace.setReport,
                () => {},
                paceVisualFilterList,
                pace.setIsRendered,
                summaryActions.setPaceVisualIsDisplay
              )}
            {!visualToggle &&
              getWidget(
                2,
                styles.paceVisual,
                pace.setReport,
                setIsPaceVisualLoaded,
                paceVisualFilterList,
                pace.setIsRendered,
                summaryActions.setPaceVisualIsDisplay
              )}
          </>
        }
        onClick={() => {
          dispatch(summaryActions.setPaceVisualIsDisplay(!paceVisualIsDisplay));
        }}
        information="Displays a 7-day forecast for Stay Dates up to 84 days ahead. Only valid for single stay date selections."
        toggleFragment={
          ENVIRONMENT.ALLOW_EXTENDED_FORECASTS?.toLowerCase() === 'true' && (
            <CustomToggleSwitch
              label="Extended Forecasts"
              size="small"
              labelPlacement="start"
              onChange={(event) =>
                dispatch(summaryActions.setSummaryVisualToggle(event.target.checked))
              }
              checked={visualToggle ?? false}
              disabled={visualToggleDisabled}
            />
          )
        }
        interpreterFragment={
          <AssistantIcon
            visualName={getVisualName(visualToggle ? 4 : 2)}
            report={pace.report}
            isProcessing={pace.isProcessing}
            setIsProcessing={pace.setIsProcessing}
            disabled={!pace.isRendered || pace.isProcessing || !paceVisualIsDisplay}
            enableSecondaryHotelGroups={enableSecondaryHotelGroups}
            exportType={exportType}
            setExportType={setExportType}
          />
        }
      />
    </>
  );
};

export default SummaryVisuals;
